import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiBarChart2,
  FiBook,
  FiClipboard,
  FiClock,
  FiFolder,
  FiTrendingUp,
  FiLogOut,
  FiMenu,
  FiMessageCircle,
  FiSearch,
  FiSend,
  FiTarget,
  FiTool,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { apiJson } from '@/lib/api';
import { COACH_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/coachOutreachRepliesStorage';
import { useCoachOutreachUnreadReplies } from '@/hooks/useCoachOutreachUnreadReplies';
import type {
  ProfessionalDirectoryProfileListResponse,
  UserResponse,
} from '@/lib/types';
import faviconPng from '@/assets/favicon.png';

type CoachOutreachRepliesListResponse = {
  replies: Array<{ received_at: string | null }>;
};

const ADMIN_PHONE = '+17148667275';
const ADMIN_DASHBOARD_HREF = import.meta.env.DEV
  ? 'http://localhost:5173/admin/'
  : 'https://harborcoparent.com/admin/';

type AdminNavItem = {
  to: string;
  label: string;
  icon: IconType;
  active: boolean;
  search?: Record<string, unknown>;
  showPendingBadge?: boolean;
  showUnreadBadge?: boolean;
  unreadBadgeLabel?: string;
};

function AdminLayout() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userQuery = useQuery({
    queryKey: ['admin', 'user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const pendingDirectoryProfilesQuery = useQuery({
    queryKey: ['admin-directory-profiles', 'pending'],
    queryFn: () =>
      apiJson<ProfessionalDirectoryProfileListResponse>(
        getToken,
        '/v1/admin/directory-profiles?status=pending',
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
  });

  const coachOutreachRepliesQuery = useQuery({
    queryKey: COACH_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<CoachOutreachRepliesListResponse>(
        getToken,
        '/v1/admin/coach-outreach/replies?limit=50',
      ),
    enabled: isLoaded && !!isSignedIn,
    retry: 1,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadCoachOutreachReplies } =
    useCoachOutreachUnreadReplies(coachOutreachRepliesQuery.data?.replies);

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/auth/sign-in' });
    }
  }, [isLoaded, isSignedIn, navigate]);

  useEffect(() => {
    if (!userQuery.isFetched || !userQuery.data) return;
    if ((userQuery.data.phone_number ?? '').trim() !== ADMIN_PHONE) {
      navigate({ to: '/', search: { hero: undefined } });
    }
  }, [userQuery.isFetched, userQuery.data, navigate]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  if (!isLoaded || userQuery.isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!isSignedIn || !userQuery.data) return null;
  if ((userQuery.data.phone_number ?? '').trim() !== ADMIN_PHONE) return null;

  const usersActive =
    pathname === '/admin' ||
    pathname === '/admin/' ||
    pathname.startsWith('/admin/users');
  const pulseActive = pathname.startsWith('/admin/pulse');
  const metricsActive = pathname.startsWith('/admin/metrics');
  const performanceActive = pathname.startsWith('/admin/performance');
  const coachOutreachActive = pathname.startsWith('/admin/coach-outreach');
  const professionalsActive = pathname.startsWith('/admin/professionals');
  const toolsActive = pathname.startsWith('/admin/tools');
  const journalActive = pathname.startsWith('/admin/journal');
  const chatsActive = pathname.startsWith('/admin/chats');
  const vaultActive = pathname.startsWith('/admin/vault');
  const adsActive = pathname.startsWith('/admin/ads');
  const attributionActive = pathname.startsWith('/admin/attribution');
  const seoActive = pathname.startsWith('/admin/seo');
  const tabBase =
    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
  const tabInactive = `${tabBase} text-gray-500 hover:text-gray-700`;
  const tabActive = `${tabBase} border border-gray-200 bg-harbor-50 text-primary`;
  const hasPendingDirectoryProfiles =
    (pendingDirectoryProfilesQuery.data?.profiles ?? []).length > 0;
  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await signOut();
      navigate({ to: '/auth/sign-in' });
    }
  };

  const navItems: AdminNavItem[] = [
    { to: '/admin', label: 'Users', icon: FiUsers, active: usersActive },
    {
      to: '/admin/pulse',
      label: 'Pulse',
      icon: FiActivity,
      active: pulseActive,
      search: { date: undefined },
    },
    {
      to: '/admin/metrics',
      label: 'Metrics',
      icon: FiTrendingUp,
      active: metricsActive,
      search: { range: '30d' },
    },
    {
      to: '/admin/performance',
      label: 'Performance',
      icon: FiClock,
      active: performanceActive,
      search: { range: '24h' },
    },
    {
      to: '/admin/ads',
      label: 'Ads',
      icon: FiBarChart2,
      active: adsActive,
      search: { days: '30' },
    },
    {
      to: '/admin/attribution',
      label: 'Attribution',
      icon: FiTarget,
      active: attributionActive,
      search: { days: '30' },
    },
    {
      to: '/admin/seo',
      label: 'SEO',
      icon: FiSearch,
      active: seoActive,
      search: { days: '30', tab: 'overview' },
    },
    {
      to: '/admin/coach-outreach',
      label: 'Coach Outreach',
      icon: FiSend,
      active: coachOutreachActive,
      showUnreadBadge: hasUnreadCoachOutreachReplies,
      unreadBadgeLabel: 'New coach replies since you last opened Replies',
    },
    {
      to: '/admin/professionals',
      label: 'Professionals',
      icon: FiClipboard,
      active: professionalsActive,
      showPendingBadge: hasPendingDirectoryProfiles,
    },
    { to: '/admin/journal', label: 'Journal', icon: FiBook, active: journalActive },
    {
      to: '/admin/chats',
      label: 'Chats',
      icon: FiMessageCircle,
      active: chatsActive,
    },
    {
      to: '/admin/vault',
      label: 'Vault',
      icon: FiFolder,
      active: vaultActive,
    },
    { to: '/admin/tools', label: 'Tools', icon: FiTool, active: toolsActive },
  ];

  const renderNavLink = (
    item: AdminNavItem,
    className: string,
    onNavigate?: () => void,
  ) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        search={item.search}
        className={className}
        onClick={onNavigate}
      >
        <Icon className='h-4 w-4 shrink-0' aria-hidden />
        {item.label}
        {item.showPendingBadge ? (
          <AdminUnreadDot label='Pending listings to review' />
        ) : null}
        {item.showUnreadBadge ? (
          <AdminUnreadDot
            label={item.unreadBadgeLabel ?? 'New items to review'}
          />
        ) : null}
      </Link>
    );
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <header className='sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-4 sm:px-6'>
        <div className='flex items-center justify-between gap-3'>
          <a
            href={ADMIN_DASHBOARD_HREF}
            className='flex min-w-0 items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg sm:gap-3 sm:text-xl'
          >
            <img
              src={faviconPng}
              alt=''
              width={36}
              height={36}
              className='h-9 w-9 shrink-0 rounded-lg border border-sky-200 bg-white object-cover'
            />
            <span className='truncate'>Harbor Admin</span>
          </a>
          <div className='flex shrink-0 items-center gap-1'>
            <button
              type='button'
              onClick={() => setMobileMenuOpen((open) => !open)}
              className='lg:hidden rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
              aria-expanded={mobileMenuOpen}
              aria-controls='admin-mobile-nav'
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? (
                <FiX className='h-6 w-6 shrink-0' aria-hidden />
              ) : (
                <FiMenu className='h-6 w-6 shrink-0' aria-hidden />
              )}
            </button>
            <button
              type='button'
              onClick={handleSignOut}
              className='flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
            >
              <FiLogOut className='h-4 w-4 shrink-0' aria-hidden />
              <span className='hidden sm:inline'>Sign out</span>
            </button>
          </div>
        </div>
        {mobileMenuOpen ? (
          <nav
            id='admin-mobile-nav'
            className='lg:hidden mt-4 flex flex-col gap-1 border-t border-gray-200 pt-4'
          >
            {navItems.map((item) =>
              renderNavLink(
                item,
                item.active
                  ? 'flex w-full items-center gap-3 rounded-lg bg-harbor-50 px-4 py-3 text-sm font-medium text-primary'
                  : 'flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100',
                () => setMobileMenuOpen(false),
              ),
            )}
          </nav>
        ) : null}
        <nav className='mt-5 hidden flex-wrap items-center gap-2 lg:flex'>
          {navItems.map((item) =>
            renderNavLink(item, item.active ? tabActive : tabInactive),
          )}
        </nav>
      </header>
      <main className='p-6'>
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});
